import React from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Grid, 
  Typography, 
  Card, 
  CardContent,
  CardMedia,
  Stack,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  Chip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import QrCodeIcon from '@mui/icons-material/QrCode';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import DevicesIcon from '@mui/icons-material/Devices';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Footer from '../components/Footer';

const LandingPage = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Features list
  const features = [
    {
      icon: <QrCodeIcon fontSize="large" color="primary" />,
      title: 'Scan & View',
      description: 'Customers scan your QR code and instantly view your menu in stunning augmented reality.'
    },
    {
      icon: <PhoneAndroidIcon fontSize="large" color="primary" />,
      title: 'No App Required',
      description: 'Works directly in web browsers. No app downloads needed for your customers.'
    },
    {
      icon: <RestaurantMenuIcon fontSize="large" color="primary" />,
      title: 'Multiple Pages',
      description: 'Present multi-page menus with rich visual content and detailed information.'
    },
    {
      icon: <DevicesIcon fontSize="large" color="primary" />,
      title: 'Cross-Platform',
      description: 'Compatible with all modern smartphones and tablets for maximum accessibility.'
    }
  ];

  // Roadmap items
  const roadmapItems = [
    {
      icon: <AutoAwesomeIcon fontSize="large" color="primary" />,
      title: 'Interactive AR Menu',
      description: 'Dishes will come alive in 3D augmented reality, offering an immersive dining preview',
      status: 'Coming Soon'
    },
    {
      icon: <FormatListBulletedIcon fontSize="large" color="primary" />,
      title: 'Direct Menu Without AR',
      description: `Standard digital menu option for devices that don't support AR technology`,
      status: 'Coming Soon'
    },
    {
      icon: <ShoppingCartIcon fontSize="large" color="primary" />,
      title: 'In-App Ordering',
      description: 'Allow customers to place orders directly through the AR menu experience',
      status: 'Planned'
    },
    {
      icon: <StorefrontIcon fontSize="large" color="primary" />,
      title: 'Analytics Dashboard',
      description: 'Track customer engagement and popular menu items with detailed analytics',
      status: 'Planned'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4efe9 100%)',
          pt: { xs: 10, md: 12 },
          pb: { xs: 12, md: 14 },
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 800,
                    mb: 2,
                    background: 'linear-gradient(45deg, #1976d2, #00796b)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Bring Your Menu to Life with AR
                </Typography>
                <Typography variant="h5" color="text.secondary" sx={{ mb: 4, fontWeight: 400 }}>
                  Transform your restaurant experience with augmented reality menus that engage customers through a simple QR code scan.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button 
                    variant="contained" 
                    size="large" 
                    component={RouterLink} 
                    to="/pricing"
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                    }}
                  >
                    View Pricing
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="large"
                    component={RouterLink}
                    to="/contact"
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                    }}
                  >
                    Contact Us
                  </Button>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  position: 'relative',
                  height: { xs: '300px', md: '450px' },
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Paper 
                  elevation={6} 
                  sx={{ 
                    position: 'absolute',
                    width: '80%', 
                    height: '80%',
                    overflow: 'hidden',
                    borderRadius: 4,
                    border: '10px solid white',
                    background: `url('https://images.unsplash.com/photo-1559339352-11d035aa65de?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                  }}
                />
                <QrCodeIcon 
                  sx={{ 
                    position: 'absolute', 
                    bottom: '10%', 
                    right: '20%', 
                    fontSize: 80,
                    opacity: 0.9,
                    color: theme.palette.primary.main,
                    filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.2))'
                  }} 
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="overline" color="primary" fontWeight="bold">
            POWERFUL FEATURES
          </Typography>
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            Enhance Your Menu Experience
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
            QResto brings your menu to life with AR technology while keeping the experience simple for both you and your customers.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 4,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                }
              }}>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" color="primary" fontWeight="bold">
              SIMPLE PROCESS
            </Typography>
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
              How QResto Works
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
              Get your restaurant menu in AR with just a few simple steps
            </Typography>
          </Box>

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box 
                sx={{ 
                  bgcolor: 'background.paper', 
                  borderRadius: 4, 
                  p: 4, 
                  boxShadow: '0 5px 15px rgba(0,0,0,0.08)'
                }}
              >
                <Stack spacing={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      minWidth: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: 'primary.main', 
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      mr: 2
                    }}>
                      1
                    </Box>
                    <Box>
                      <Typography variant="h6" gutterBottom>Share Your Menu Details</Typography>
                      <Typography variant="body1" color="text.secondary">
                        Send us your menu details, images, and any special requirements you have.
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      minWidth: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: 'primary.main', 
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      mr: 2
                    }}>
                      2
                    </Box>
                    <Box>
                      <Typography variant="h6" gutterBottom>We Create Your AR Experience</Typography>
                      <Typography variant="body1" color="text.secondary">
                        Our team builds your custom AR menu experience with beautiful visuals and intuitive navigation.
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      minWidth: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: 'primary.main', 
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      mr: 2
                    }}>
                      3
                    </Box>
                    <Box>
                      <Typography variant="h6" gutterBottom>Receive Your Custom QR Code</Typography>
                      <Typography variant="body1" color="text.secondary">
                        We deliver your unique QR code that customers can scan to view your menu in AR.
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      minWidth: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: 'primary.main', 
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      mr: 2
                    }}>
                      4
                    </Box>
                    <Box>
                      <Typography variant="h6" gutterBottom>Delight Your Customers</Typography>
                      <Typography variant="body1" color="text.secondary">
                        Watch as customers engage with your menu in a whole new way, enhancing their dining experience.
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={7} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1533777857889-4be7c70b33f7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
                alt="Restaurant customer using phone"
                sx={{
                  width: '100%',
                  borderRadius: 4,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Future Roadmap Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="overline" color="primary" fontWeight="bold">
            ROADMAP
          </Typography>
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            Coming Soon
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
            We're constantly improving QResto with exciting new features
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {roadmapItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                position: 'relative',
                overflow: 'visible'
              }}>
                <CardContent sx={{ flexGrow: 1, pt: 5 }}>
                  <Chip 
                    label={item.status} 
                    color={item.status === 'Coming Soon' ? 'primary' : 'secondary'}
                    size="small"
                    sx={{ 
                      position: 'absolute', 
                      top: -10, 
                      right: 16,
                      fontWeight: 'medium'
                    }}
                  />
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    {item.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight="medium">
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        bgcolor: 'primary.main',
        color: 'white',
        py: { xs: 8, md: 10 },
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
              Ready to Transform Your Menu Experience?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join restaurants already delighting their customers with AR menus
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              component={RouterLink}
              to="/contact"
              sx={{ 
                bgcolor: 'white', 
                color: '#003366', // Dark navy blue for maximum contrast
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  color: '#001a33', // Even darker on hover
                },
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', // Adding shadow for more definition
              }}
            >
              Get Started Now
            </Button>
          </Box>
        </Container>
        {/* Visual elements in the background */}
        <Box sx={{ 
          position: 'absolute', 
          top: -30, 
          left: -30, 
          width: 200, 
          height: 200, 
          bgcolor: 'rgba(255,255,255,0.05)',
          borderRadius: '50%' 
        }} />
        <Box sx={{ 
          position: 'absolute', 
          bottom: -50, 
          right: -50, 
          width: 250, 
          height: 250, 
          bgcolor: 'rgba(255,255,255,0.08)',
          borderRadius: '50%' 
        }} />
      </Box>

      <Footer />
    </>
  );
};

export default LandingPage;